// Copyright (C) 2025-2026 Skylark Drones

import apiInstance, { v1EventGatewayInstance } from '@/config'
import store from '@/store'

export function getAllDocks() {
  const companyId = store.getters.currentActiveOrganizationId
  return apiInstance.http.get(`/company/${companyId}/docks`)
}
/**
 * Get state of the docks
 *
 * @param {Array} docksSnList List of dock sn
 * @returns {Promise}
 */
export function getDockState(docksSnList) {
  return v1EventGatewayInstance.http.get('docks/states', {
    params: {
      device_sn_list: JSON.stringify(docksSnList)
    }
  })
}

export function startLivestream(dockSn, { channelName, videoId }) {
  return v1EventGatewayInstance.http.post(`docks/${dockSn}/livestreams/start`, {
    channel_name: channelName,
    video_id: videoId
  })
}

/**
 * Send a manual alert for a dock.
 * @param {string} dockId
 * @param {Object} payload
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export function createDockAlert(dockId, payload) {
  const companyId = store.getters.currentActiveOrganizationId
  return apiInstance.http.post(
    `company/${companyId}/docks/${dockId}/alerts`,
    payload
  )
}

/**
 * Get static info of dock by sn or id
 *
 * @param {string} dockId
 * @returns {Promise}
 */
export function getDock(dockId) {
  const companyId = store.getters.currentActiveOrganizationId
  return apiInstance.http.get(`company/${companyId}/docks/${dockId}`)
}

export function issueDockMission(dockId, missionId) {
  const companyId = store.getters.currentActiveOrganizationId
  return apiInstance.http.post(
    `company/${companyId}/docks/${dockId}/missions/${missionId}/issue`
  )
}

export function requestAuthority(dockSn, { domain, userId, payloadIndex }) {
  const payload = {
    user_id: userId,
    domain
  }

  if (payloadIndex) {
    payload.payload_index = payloadIndex
  }
  return v1EventGatewayInstance.http.post(
    `docks/${dockSn}/authority_request`,
    payload
  )
}

export function replyToAuthorityRequest(
  dockSn,
  { domain, userId, authorityHolderId, accepted, payloadIndex }
) {
  const payload = {
    user_id: userId,
    domain,
    accepted
  }
  if (payloadIndex) {
    payload.payload_index = payloadIndex
  }
  return v1EventGatewayInstance.http.post(
    `docks/${dockSn}/authority_request/action?authority_holder_id=${authorityHolderId}`,
    payload
  )
}

/**
 * Edit a dock
 * @param {String} id object id of dock
 * @param {Object} data changes to be sent in the payload
 * @param {String} etag ETag of the dock object
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export function updateDock(dockId, payload, etag) {
  const companyId = store.getters.currentActiveOrganizationId
  return apiInstance.http.patch(
    `company/${companyId}/docks/${dockId}`,
    payload,
    { headers: { 'If-Match': etag } }
  )
}

/**
 * Get geozones of a dock
 * @param {String} id object id of the dock
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export function getDockGeozones(id) {
  const companyId = store.getters.currentActiveOrganizationId
  return apiInstance.http.get(`/company/${companyId}/docks/${id}/geozones`)
}

/**
 * Get annotations of a dock
 * @param {String} dockId object id of the dock
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export function getDockAnnotations(dockId) {
  const companyId = store.getters.currentActiveOrganizationId
  return apiInstance.http.get(
    `/company/${companyId}/docks/${dockId}/annotations`
  )
}

/**
 * Save annotation of a dock
 * @param {String} dockId object id of the dock
 * @param {Array<Object>} annotations annotations to be saved
 * @param {String} annotations[].label name of the annotation
 * @param {String} annotations[].color color hex of the annotation
 * @param {Object} annotations[].geometry geometry of the annotation
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export function saveDockAnnotation(dockId, annotations) {
  const companyId = store.getters.currentActiveOrganizationId
  return apiInstance.http.post(
    `/company/${companyId}/docks/${dockId}/annotations`,
    { annotations }
  )
}

/**
 * Delete annotation by id
 * @param {String} dockId object id of the dock
 * @param {String} annotationId object id of the annotation
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export function deleteDockAnnotation(dockId, annotationId) {
  const companyId = store.getters.currentActiveOrganizationId
  return apiInstance.http.delete(
    `/company/${companyId}/docks/${dockId}/annotations/${annotationId}`
  )
}

/**
 * Sync geozones to a dock
 * @param {String} dockSn Serial number of the dock
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export function syncGeozones(dockSn) {
  return v1EventGatewayInstance.http.post(`docks/${dockSn}/geozones/sync`)
}
